import React, { useState } from 'react';
import { AgentHealth } from '../types';
import './SystemHealth.css';

interface SystemHealthProps {
  agents: AgentHealth[];
  onRestartAgent: (agentId: string) => void;
}

const SystemHealth: React.FC<SystemHealthProps> = ({ agents, onRestartAgent }) => {
  const [selectedAgent, setSelectedAgent] = useState<AgentHealth | null>(null);
  const [showVectorStoreDetails, setShowVectorStoreDetails] = useState(false);
  
  // Debug logging
  console.log('SystemHealth agents:', agents);
  if (agents.length > 0) {
    console.log('First agent vectorStore:', agents[0].vectorStore);
  }
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy':
        return '#4caf50';
      case 'unhealthy':
        return '#ff9800';
      case 'offline':
        return '#f44336';
      default:
        return '#9e9e9e';
    }
  };

  const formatUptime = (seconds: number) => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  const getReadinessThresholds = (agentType?: string) => {
    // Define minimum document counts for each category per agent type
    const thresholds: Record<string, Record<string, number>> = {
      financial_analyst: { banking: 50, market: 30, customers: 20, technology: 10 },
      market_intel: { market: 100, trends: 50, competitors: 40, cryptocurrency: 30, stablecoin: 25 },
      risk_analyst: { banking: 40, market: 30, trends: 20, technology: 15 },
      compliance_analyst: { banking: 60, market: 20, cryptocurrency: 15, stablecoin: 15 },
      cx_insights: { customers: 80, banking: 30, market: 20, technology: 10 },
      it_analyst: { technology: 70, banking: 30, cryptocurrency: 20, stablecoin: 15 },
      strategy: { banking: 40, market: 40, trends: 30, competitors: 30, customers: 20, technology: 20 }
    };
    
    return thresholds[agentType || 'strategy'] || thresholds.strategy;
  };

  const handleVectorStoreClick = (agent: AgentHealth) => {
    setSelectedAgent(agent);
    setShowVectorStoreDetails(true);
  };

  return (
    <div className="system-health">
      <h2>System Health ({agents.length} agents)</h2>
      <div className="agents-grid">
        {agents.map(agent => (
          <div key={agent.agentId} className="agent-card">
            <div className="agent-header">
              <h3>{agent.agentName}</h3>
              <div 
                className="status-indicator" 
                style={{ backgroundColor: getStatusColor(agent.status) }}
              />
            </div>
            
            <div className="agent-metrics">
              <div className="metric">
                <span className="metric-label">Status:</span>
                <span className={`metric-value status-${agent.status}`}>
                  {agent.status}
                </span>
              </div>
              
              {agent.uptime !== undefined && (
                <div className="metric">
                  <span className="metric-label">Uptime:</span>
                  <span className="metric-value">{formatUptime(agent.uptime)}</span>
                </div>
              )}
              
              <div className="metric">
                <span className="metric-label">CPU:</span>
                <span className="metric-value">{(agent.cpu || agent.cpuUsage || 0).toFixed(1)}%</span>
                <div className="metric-bar">
                  <div 
                    className="metric-bar-fill cpu"
                    style={{ width: `${agent.cpu || agent.cpuUsage || 0}%` }}
                  />
                </div>
              </div>
              
              <div className="metric">
                <span className="metric-label">Memory:</span>
                <span className="metric-value">{(agent.memory || agent.memoryUsage || 0).toFixed(1)}%</span>
                <div className="metric-bar">
                  <div 
                    className="metric-bar-fill memory"
                    style={{ width: `${agent.memory || agent.memoryUsage || 0}%` }}
                  />
                </div>
              </div>
              
              <div className="metric">
                <span className="metric-label">Active Requests:</span>
                <span className="metric-value">{agent.activeRequests}</span>
              </div>
              
              <div className="metric">
                <span className="metric-label">Last Check:</span>
                <span className="metric-value">
                  {agent.lastHealthCheck || agent.lastHeartbeat 
                    ? new Date(agent.lastHealthCheck || agent.lastHeartbeat || '').toLocaleTimeString()
                    : 'N/A'}
                </span>
              </div>
              
              {agent.vectorStore ? (
                <>
                  <div className="metric">
                    <span className="metric-label">Vector Store:</span>
                    <span className={`metric-value ${agent.vectorStore.readinessScore >= 80 ? 'status-ready' : agent.vectorStore.readinessScore >= 50 ? 'status-partial' : 'status-insufficient'}`}>
                      {agent.vectorStore.readinessScore >= 80 ? 'Ready' : agent.vectorStore.readinessScore >= 50 ? 'Partial' : 'Insufficient'}
                    </span>
                  </div>
                  
                  <div className="metric">
                    <span className="metric-label">Documents:</span>
                    <span className="metric-value">{agent.vectorStore.documentCount.toLocaleString()}</span>
                  </div>
                  
                  <div className="metric clickable" onClick={() => handleVectorStoreClick(agent)}>
                    <span className="metric-label">Readiness:</span>
                    <span className="metric-value">{agent.vectorStore.readinessScore}%</span>
                    <div className="metric-bar">
                      <div 
                        className={`metric-bar-fill ${agent.vectorStore.readinessScore >= 80 ? 'ready' : agent.vectorStore.readinessScore >= 50 ? 'partial' : 'insufficient'}`}
                        style={{ width: `${agent.vectorStore.readinessScore}%` }}
                      />
                    </div>
                    <span className="click-hint">Click for details</span>
                  </div>
                </>
              ) : (
                <div className="metric">
                  <span className="metric-label">Vector Store:</span>
                  <span className="metric-value">No data</span>
                </div>
              )}
            </div>
            
            <div className="agent-actions">
              <button 
                className="action-button restart"
                onClick={() => onRestartAgent(agent.agentId)}
                disabled={agent.status === 'offline'}
              >
                Restart
              </button>
            </div>
          </div>
        ))}
      </div>
      
      {showVectorStoreDetails && selectedAgent && selectedAgent.vectorStore && (
        <div className="modal-overlay" onClick={() => setShowVectorStoreDetails(false)}>
          <div className="modal-content vector-store-details" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Vector Store Details - {selectedAgent.agentName}</h3>
              <button className="close-button" onClick={() => setShowVectorStoreDetails(false)}>×</button>
            </div>
            
            <div className="modal-body">
              <div className="readiness-summary">
                <h4>Overall Readiness: {selectedAgent.vectorStore.readinessScore}%</h4>
                <div className="readiness-bar">
                  <div 
                    className={`readiness-fill ${selectedAgent.vectorStore.readinessScore >= 80 ? 'ready' : selectedAgent.vectorStore.readinessScore >= 50 ? 'partial' : 'insufficient'}`}
                    style={{ width: `${selectedAgent.vectorStore.readinessScore}%` }}
                  />
                </div>
                <p className="readiness-status">
                  {selectedAgent.vectorStore.readinessScore >= 80 
                    ? '✅ Agent has sufficient knowledge to respond effectively to most queries'
                    : selectedAgent.vectorStore.readinessScore >= 50
                    ? '⚠️ Agent has partial knowledge - may struggle with some query types'
                    : '❌ Agent needs more documents to be effective'}
                </p>
              </div>
              
              <div className="category-breakdown">
                <h4>Knowledge Categories</h4>
                {Object.entries(selectedAgent.vectorStore.categories).map(([category, count]) => {
                  const threshold = getReadinessThresholds(selectedAgent.agentType)[category] || 20;
                  const percentage = Math.min(100, (count / threshold) * 100);
                  
                  return (
                    <div key={category} className="category-item">
                      <div className="category-header">
                        <span className="category-name">{category.charAt(0).toUpperCase() + category.slice(1)}</span>
                        <span className="category-count">{count} / {threshold} docs</span>
                      </div>
                      <div className="category-bar">
                        <div 
                          className={`category-fill ${percentage >= 100 ? 'complete' : percentage >= 50 ? 'partial' : 'insufficient'}`}
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
              
              <div className="readiness-info">
                <h4>Readiness Criteria</h4>
                <p>The agent is considered ready when it has sufficient documents in key categories:</p>
                <ul>
                  <li><strong>Banking:</strong> Core banking operations, FAB-specific information</li>
                  <li><strong>Market:</strong> Market analysis, financial trends, economic data</li>
                  <li><strong>Trends:</strong> Industry trends, emerging technologies, future outlook</li>
                  <li><strong>Competitors:</strong> Competitive analysis, benchmarking data</li>
                  <li><strong>Customers:</strong> Customer insights, sentiment, behavior patterns</li>
                  <li><strong>Technology:</strong> Tech infrastructure, digital solutions, innovations</li>
                  <li><strong>Cryptocurrency/Stablecoin:</strong> Digital asset knowledge, regulations</li>
                </ul>
              </div>
              
              {selectedAgent.vectorStore.lastUpdate && (
                <div className="last-update">
                  <p>Last Updated: {new Date(selectedAgent.vectorStore.lastUpdate).toLocaleString()}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SystemHealth;